import { useSyncExternalStore } from 'react';
import { matchRoute, normalizePath, type RouteId } from './paths';

/**
 * Tarayıcı geçmişine bağlanan ince katman.
 *
 * Eşleme mantığının tamamı `paths.ts` içinde ve saf; burada yalnızca "adres
 * değişti, yeniden render et" bağı var.
 *
 * ## Neden `useSyncExternalStore`
 *
 * Alternatif `useState` + `useEffect(() => addEventListener('popstate'))` idi.
 * O desen, olay dinleyicisi bağlanmadan ÖNCE gerçekleşen bir gezinmeyi kaçırır
 * (React 18 eşzamanlı render'da mount ile efekt arasında gerçek bir pencere
 * var) ve iki bileşen ayrı state tuttuğu için geçici olarak farklı adresler
 * gösterebilirdi. `useSyncExternalStore` tek bir dış kaynağı okur — yırtılma
 * (tearing) tanım gereği imkânsız.
 */

/**
 * `pushState`/`replaceState` bir olay YAYMAZ — `popstate` yalnızca geri/ileri
 * tuşunda tetiklenir. Kendi gezinmemizi duyurmak için kendi olayımız gerekiyor.
 */
const NAVIGATION_EVENT = 'zee:navigation';

function subscribe(onStoreChange: () => void): () => void {
  window.addEventListener('popstate', onStoreChange);
  window.addEventListener(NAVIGATION_EVENT, onStoreChange);
  return () => {
    window.removeEventListener('popstate', onStoreChange);
    window.removeEventListener(NAVIGATION_EVENT, onStoreChange);
  };
}

function getPathnameSnapshot(): string {
  return window.location.pathname;
}

function getSearchSnapshot(): string {
  return window.location.search;
}

/* Sunucu anlık görüntüsü: SPA istemcide çalışıyor, ama `useSyncExternalStore`
   üçüncü argüman olmadan sunucu render'ında patlar. Sabit bir değer vermek
   ileride SSR eklenirse ilk render'ı ana sayfaya sabitler. */
function getHomeSnapshot(): string {
  return '/';
}

function getEmptySnapshot(): string {
  return '';
}

export function usePathname(): string {
  return useSyncExternalStore(subscribe, getPathnameSnapshot, getHomeSnapshot);
}

export function useSearch(): string {
  return useSyncExternalStore(subscribe, getSearchSnapshot, getEmptySnapshot);
}

export function useRoute(): RouteId {
  return matchRoute(usePathname());
}

interface NavigateOptions {
  /** Geçmişe yeni kayıt eklemek yerine mevcut kaydı değiştirir. */
  replace?: boolean;
}

/**
 * Sayfa değiştirir.
 *
 * Sayfa başına kaydırma BİLİNÇLİ: `pushState` kaydırma konumunu korur, yani
 * uzun bir sayfanın dibinden yeni bir sayfaya geçince kullanıcı o sayfanın da
 * dibinde açılır ve içeriğin başını hiç görmez. Geri/ileri tuşunda ise
 * tarayıcının kendi geri yükleme davranışı doğrudur; oraya karışmıyoruz.
 */
export function navigate(to: string, options: NavigateOptions = {}): void {
  const isSamePage = normalizePath(to) === normalizePath(window.location.pathname);

  if (options.replace === true) window.history.replaceState(null, '', to);
  else window.history.pushState(null, '', to);

  if (!isSamePage) window.scrollTo(0, 0);

  window.dispatchEvent(new Event(NAVIGATION_EVENT));

  /* Tek sayfa site: `/#iletisim` gibi bir bağlantı bölüme kaydırmalı.
     `pushState` tarayıcının kendi çapa kaydırmasını TETİKLEMEZ. */
  const hashIndex = to.indexOf('#');
  if (hashIndex >= 0) scrollToHash(to.slice(hashIndex));
}

/** Bir bölüm en fazla bu kadar denemede çizilmiş olmalı (≈1,5 sn). */
const HASH_SCROLL_ATTEMPTS = 30;
const HASH_SCROLL_INTERVAL_MS = 50;

/**
 * Çapadaki bölüme kaydırır.
 *
 * Bölüm henüz DOM'da olmayabilir: başka bir sayfadan ana sayfaya dönülürken
 * React bir sonraki karede çizer. Bu yüzden kısa aralıklarla birkaç kez
 * dener; bulamazsa sessizce vazgeçer (sayfa yine açılmış olur).
 * Hareket hassasiyeti açıksa yumuşak kaydırma kullanılmaz.
 */
export function scrollToHash(hash: string, attemptsLeft = HASH_SCROLL_ATTEMPTS): void {
  const id = decodeURIComponent(hash.replace(/^#/, ''));
  if (id === '') return;

  const target = document.getElementById(id);
  if (target !== null) {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    return;
  }
  if (attemptsLeft > 0) {
    window.setTimeout(() => scrollToHash(hash, attemptsLeft - 1), HASH_SCROLL_INTERVAL_MS);
  }
}
